import type { CompanyData } from 'src/common/interfaces/companies';

type FeatureValue = boolean | { enabled?: boolean } | undefined;

/**
 * Feature names matching the CompanyFeature enum values from the API.
 * These values correspond to the feature keys in the company features object.
 */
export enum CompanyFeatureName {
  THM_CONTENT = 'thmContent',
  ADDITIONAL_CONTENT = 'additionalContentAccess',
  PATHS = 'paths',
  SOC_SIM = 'socSim',
  THREAT_HUNTING = 'threatHunting',
  TTX = 'ttx',
  CERTIFICATIONS = 'certifications',
  ASSIGNMENTS = 'assignments',
  CONTENT_STUDIO = 'contentStudio',
  CTF_BUILDER = 'ctfBuilder',
  SKILLS_MATRIX = 'skillsMatrix',
  SKILLS_GRAPH = 'skillsGraph',
  MITRE = 'mitre',
  SSO = 'sso',
  API_ACCESS = 'apiAccess',
  FLEXIBLE_SEATS = 'hasFlexibleSeats',
  PRIVATE_GROUP = 'privateGroup',
  DISABLE_VM_INTERNET = 'disableVmInternet',
}

/**
 * Checks if a company feature is enabled.
 * Handles both direct boolean features and nested features with an `enabled` property.
 *
 * @param features - The features object from company data
 * @param featureName - The name of the feature to check (e.g., CompanyFeatureName.PATHS, CompanyFeatureName.SOC_SIM)
 * @returns true if the feature is enabled, false otherwise
 */
export const isFeatureEnabled = (
  features: CompanyData['features'] | null | undefined,
  featureName: CompanyFeatureName,
): boolean => {
  if (!features) return false;

  const featureValue = features[featureName as keyof typeof features] as FeatureValue;

  if (featureValue === undefined || featureValue === null) return false;

  // Handle nested features with `enabled` property (e.g., privateGroup.enabled)
  if (typeof featureValue === 'object' && 'enabled' in featureValue) {
    return Boolean(featureValue.enabled);
  }

  return Boolean(featureValue);
};
